// Pessoas de um cadastro — representantes legais, responsaveis e usuario master.
//
// Um componente so, usado pelos modais de prefeitura E de banco: o contrato da
// API e' identico dos dois lados (muda so o segmento da URL), entao duplicar
// isso em duas telas seria manter dois formularios iguais em paralelo.
//
// Salva DIRETO na API, item a item — nao entra no payload do "Salvar" do modal.
// Motivo: sao colecoes independentes; se fossem arrays no corpo do POST do
// tenant, salvar o endereco viraria um replace destrutivo da lista de
// responsaveis. Como consequencia, a secao so aparece com o tenant ja criado
// (precisa de id) — no cadastro novo mostra o aviso pra salvar antes.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, CepField, CpfField, FormGrid, IconButton, SelectField, TelefoneField, TextField,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import type {
  CadastroMaster, CadastroMasterInput,
  CadastroRepresentante, CadastroRepresentanteInput,
  CadastroResponsavel, CadastroResponsavelInput,
  CadastroResponsavelTipo, CadastroTenantKind,
} from "@atlas/sdk";

// Rotulos dos papeis definidos pelo cliente (1=rh ... 5=operacional). A API
// grava o label; o codigo numerico so existe pra payload do site institucional.
const TIPOS_RESPONSAVEL: { value: CadastroResponsavelTipo; label: string }[] = [
  { value: "rh", label: "RH" },
  { value: "importacao", label: "Importação" },
  { value: "financeiro", label: "Financeiro" },
  { value: "financeiro_banco", label: "Financeiro (consignatária)" },
  { value: "operacional", label: "Operacional" },
];

const tituloSecao: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
  color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 10,
};
const cardSecao: React.CSSProperties = {
  padding: 14, borderRadius: 10, border: "1px solid var(--border)",
  background: "var(--bg-elev)", marginTop: 12,
};
const linhaItem: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  gap: 8, padding: "8px 10px", borderRadius: 8,
  border: "1px solid var(--border)", background: "var(--bg-elev-2)", marginBottom: 6,
};

/** Extrai a mensagem util de um erro da API (os detalhes por campo do
 *  validation_error sao mais informativos que o "Dados invalidos" generico). */
function mensagemDeErro(e: unknown): string {
  const err = e as { details?: Record<string, unknown>; message?: string };
  const det = err?.details;
  if (det && typeof det === "object") {
    const partes = Object.values(det).filter((v): v is string => typeof v === "string");
    if (partes.length) return partes.join(" | ");
  }
  return err?.message ?? "Erro ao salvar";
}

export function CadastroPessoas({ kind, tenantId, somenteLeitura = false }: {
  kind: CadastroTenantKind;
  tenantId?: number;
  /** Modo visualizar do modal-pai: some com adicionar/editar/remover, mas as
   *  listas continuam visiveis — representantes, responsaveis e master sao
   *  justamente o que o operador abriu a tela pra consultar. */
  somenteLeitura?: boolean;
}) {
  if (!tenantId) {
    return (
      <div style={{ ...cardSecao, color: "var(--text-muted)", fontSize: 12 }}>
        Representantes legais, responsáveis e usuário master ficam disponíveis
        depois que este cadastro for salvo.
      </div>
    );
  }
  return (
    <>
      <SecaoRepresentantes kind={kind} tenantId={tenantId} somenteLeitura={somenteLeitura} />
      <SecaoResponsaveis kind={kind} tenantId={tenantId} somenteLeitura={somenteLeitura} />
      <SecaoMaster kind={kind} tenantId={tenantId} somenteLeitura={somenteLeitura} />
    </>
  );
}

type SecaoProps = { kind: CadastroTenantKind; tenantId: number; somenteLeitura: boolean };

// ============================================================
// Representantes legais
// ============================================================

const REPRESENTANTE_VAZIO: CadastroRepresentanteInput = {
  nomeCompleto: "", cpf: "", rg: "", nacionalidade: "", cargo: "", estadoCivil: "",
  endereco: "", cep: "", complemento: "", bairro: "", cidade: "", uf: "",
};

function SecaoRepresentantes({ kind, tenantId, somenteLeitura }: SecaoProps) {
  const qc = useQueryClient();
  const chave = ["admin", kind, tenantId, "representantes"];
  const lista = useQuery({ queryKey: chave, queryFn: () => atlas.admin.listRepresentantes(kind, tenantId) });
  const [editando, setEditando] = useState<number | "novo" | null>(null);
  const [form, setForm] = useState<CadastroRepresentanteInput>(REPRESENTANTE_VAZIO);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: () => atlas.admin.salvarRepresentante(
      kind, tenantId, form, typeof editando === "number" ? editando : undefined,
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chave });
      setEditando(null);
      setErro(null);
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: (id: number) => atlas.admin.removerRepresentante(kind, tenantId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: chave }),
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  const abrir = (r?: CadastroRepresentante) => {
    setErro(null);
    setEditando(r ? r.id : "novo");
    setForm(r ? { ...REPRESENTANTE_VAZIO, ...r, id: undefined } as CadastroRepresentanteInput : REPRESENTANTE_VAZIO);
  };

  return (
    <div style={cardSecao}>
      <div style={tituloSecao}>Representantes legais</div>
      {(lista.data?.representantes ?? []).map((r) => (
        <div key={r.id} style={linhaItem}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{r.nomeCompleto || "(sem nome)"}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {[r.cargo, r.cpf].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
          {!somenteLeitura ? (
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              <IconButton title="Editar" onClick={() => abrir(r)}>✎</IconButton>
              <IconButton title="Remover" onClick={() => remover.mutate(r.id)}>🗑</IconButton>
            </div>
          ) : null}
        </div>
      ))}
      {lista.data && lista.data.representantes.length === 0 && editando === null ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>Nenhum representante cadastrado.</div>
      ) : null}

      {editando !== null ? (
        <div style={{ marginTop: 8 }}>
          <FormGrid cols={2}>
            <TextField label="Nome completo" value={form.nomeCompleto ?? ""} onChange={(e) => setForm({ ...form, nomeCompleto: e.target.value })} />
            <CpfField label="CPF" value={form.cpf ?? ""} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
            <TextField label="RG" value={form.rg ?? ""} onChange={(e) => setForm({ ...form, rg: e.target.value })} />
            <TextField label="Nacionalidade" value={form.nacionalidade ?? ""} onChange={(e) => setForm({ ...form, nacionalidade: e.target.value })} />
            <TextField label="Cargo" value={form.cargo ?? ""} onChange={(e) => setForm({ ...form, cargo: e.target.value })} />
            <TextField label="Estado civil" value={form.estadoCivil ?? ""} onChange={(e) => setForm({ ...form, estadoCivil: e.target.value })} />
            <TextField label="Endereço" value={form.endereco ?? ""} onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
            <CepField label="CEP" value={form.cep ?? ""} onChange={(e) => setForm({ ...form, cep: e.target.value })} />
            <TextField label="Complemento" value={form.complemento ?? ""} onChange={(e) => setForm({ ...form, complemento: e.target.value })} />
            <TextField label="Bairro" value={form.bairro ?? ""} onChange={(e) => setForm({ ...form, bairro: e.target.value })} />
            <TextField label="Cidade" value={form.cidade ?? ""} onChange={(e) => setForm({ ...form, cidade: e.target.value })} />
            <TextField label="UF" value={form.uf ?? ""} maxLength={2} onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })} />
          </FormGrid>
          {erro ? <div style={{ color: "var(--danger-500)", fontSize: 12, marginTop: 8 }}>{erro}</div> : null}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Button type="button" onClick={() => salvar.mutate()} disabled={salvar.isPending}>
              {salvar.isPending ? "Salvando…" : "Salvar representante"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setEditando(null); setErro(null); }}>Cancelar</Button>
          </div>
        </div>
      ) : somenteLeitura ? null : (
        <Button type="button" variant="ghost" onClick={() => abrir()}>+ Adicionar representante</Button>
      )}
    </div>
  );
}

// ============================================================
// Responsaveis
// ============================================================

const RESPONSAVEL_VAZIO: CadastroResponsavelInput = {
  nomeCompleto: "", cpf: "", telefone: "", email: "", tipo: "rh",
};

function SecaoResponsaveis({ kind, tenantId, somenteLeitura }: SecaoProps) {
  const qc = useQueryClient();
  const chave = ["admin", kind, tenantId, "responsaveis"];
  const lista = useQuery({ queryKey: chave, queryFn: () => atlas.admin.listResponsaveis(kind, tenantId) });
  const [editando, setEditando] = useState<number | "novo" | null>(null);
  const [form, setForm] = useState<CadastroResponsavelInput>(RESPONSAVEL_VAZIO);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: () => atlas.admin.salvarResponsavel(
      kind, tenantId, form, typeof editando === "number" ? editando : undefined,
    ),
    onSuccess: () => { qc.invalidateQueries({ queryKey: chave }); setEditando(null); setErro(null); },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: (id: number) => atlas.admin.removerResponsavel(kind, tenantId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: chave }),
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  const abrir = (r?: CadastroResponsavel) => {
    setErro(null);
    setEditando(r ? r.id : "novo");
    setForm(r ? { ...RESPONSAVEL_VAZIO, ...r, id: undefined } as CadastroResponsavelInput : RESPONSAVEL_VAZIO);
  };

  return (
    <div style={cardSecao}>
      <div style={tituloSecao}>Responsáveis por área</div>
      {(lista.data?.responsaveis ?? []).map((r) => (
        <div key={r.id} style={linhaItem}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{r.nomeCompleto || "(sem nome)"}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {[TIPOS_RESPONSAVEL.find((t) => t.value === r.tipo)?.label, r.email].filter(Boolean).join(" · ") || "—"}
            </div>
          </div>
          {!somenteLeitura ? (
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              <IconButton title="Editar" onClick={() => abrir(r)}>✎</IconButton>
              <IconButton title="Remover" onClick={() => remover.mutate(r.id)}>🗑</IconButton>
            </div>
          ) : null}
        </div>
      ))}
      {lista.data && lista.data.responsaveis.length === 0 && editando === null ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>Nenhum responsável cadastrado.</div>
      ) : null}

      {editando !== null ? (
        <div style={{ marginTop: 8 }}>
          <FormGrid cols={2}>
            <TextField label="Nome completo" value={form.nomeCompleto ?? ""} onChange={(e) => setForm({ ...form, nomeCompleto: e.target.value })} />
            <CpfField label="CPF" value={form.cpf ?? ""} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
            <TelefoneField label="Telefone" value={form.telefone ?? ""} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
            <TextField label="E-mail" type="email" value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <SelectField
              label="Área"
              value={form.tipo ?? "rh"}
              options={TIPOS_RESPONSAVEL}
              onChange={(e) => setForm({ ...form, tipo: e.target.value as CadastroResponsavelTipo })}
            />
          </FormGrid>
          {erro ? <div style={{ color: "var(--danger-500)", fontSize: 12, marginTop: 8 }}>{erro}</div> : null}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Button type="button" onClick={() => salvar.mutate()} disabled={salvar.isPending}>
              {salvar.isPending ? "Salvando…" : "Salvar responsável"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setEditando(null); setErro(null); }}>Cancelar</Button>
          </div>
        </div>
      ) : somenteLeitura ? null : (
        <Button type="button" variant="ghost" onClick={() => abrir()}>+ Adicionar responsável</Button>
      )}
    </div>
  );
}

// ============================================================
// Master (1:1)
// ============================================================

const MASTER_VAZIO: CadastroMasterInput = { nomeCompleto: "", cpf: "", celular: "", email: "" };

function SecaoMaster({ kind, tenantId, somenteLeitura }: SecaoProps) {
  const qc = useQueryClient();
  const chave = ["admin", kind, tenantId, "master"];
  const atual = useQuery({ queryKey: chave, queryFn: () => atlas.admin.getMaster(kind, tenantId) });
  // Editando so quando o usuario abre — fora disso a secao mostra o resumo.
  const [form, setForm] = useState<CadastroMasterInput | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: () => atlas.admin.salvarMaster(kind, tenantId, form ?? MASTER_VAZIO),
    onSuccess: () => { qc.invalidateQueries({ queryKey: chave }); setForm(null); setErro(null); },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: () => atlas.admin.removerMaster(kind, tenantId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: chave }); setForm(null); },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  const m: CadastroMaster | null = atual.data?.master ?? null;
  const abrir = () => {
    setErro(null);
    setForm(m ? { ...MASTER_VAZIO, ...m, id: undefined } as CadastroMasterInput : MASTER_VAZIO);
  };

  return (
    <div style={cardSecao}>
      <div style={tituloSecao}>Usuário master</div>
      {form === null ? (
        <>
          {m ? (
            <div style={linhaItem}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{m.nomeCompleto || "(sem nome)"}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {[m.email, m.cpf].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              {!somenteLeitura ? (
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <IconButton title="Editar" onClick={abrir}>✎</IconButton>
                  <IconButton title="Remover" onClick={() => remover.mutate()}>🗑</IconButton>
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
              Nenhum usuário master definido. É o dono da conta — um por cadastro.
            </div>
          )}
          {!m && !somenteLeitura ? <Button type="button" variant="ghost" onClick={abrir}>+ Definir usuário master</Button> : null}
        </>
      ) : (
        <div>
          <FormGrid cols={2}>
            <TextField label="Nome completo" value={form.nomeCompleto ?? ""} onChange={(e) => setForm({ ...form, nomeCompleto: e.target.value })} />
            <CpfField label="CPF" value={form.cpf ?? ""} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
            <TelefoneField label="Celular" value={form.celular ?? ""} onChange={(e) => setForm({ ...form, celular: e.target.value })} />
            <TextField label="E-mail" type="email" value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </FormGrid>
          {erro ? <div style={{ color: "var(--danger-500)", fontSize: 12, marginTop: 8 }}>{erro}</div> : null}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Button type="button" onClick={() => salvar.mutate()} disabled={salvar.isPending}>
              {salvar.isPending ? "Salvando…" : "Salvar master"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setForm(null); setErro(null); }}>Cancelar</Button>
          </div>
        </div>
      )}
    </div>
  );
}
